import { type AggResponse, fetchAgg } from '../../../common/utils/fetch-agg';
import { inMemoryCache } from '../../../utils/caching/in-memory';

export const positionJobFamiliesCache: {
	get: (key: string) => Promise<AggResponse<PositionJobFamiliesResponse>> | undefined;
	set: (key: string, result: Promise<AggResponse<PositionJobFamiliesResponse>>) => void;
	has: (key: string) => boolean;
	clear: (key?: string) => void;
	inMemoryDecorator: (
		key: string,
		supplier: () => Promise<AggResponse<PositionJobFamiliesResponse>>,
		expireWhen?: ((result: AggResponse<PositionJobFamiliesResponse>) => boolean) | undefined,
	) => Promise<AggResponse<PositionJobFamiliesResponse>>;
} = inMemoryCache<AggResponse<PositionJobFamiliesResponse>>();

const QUERY_NAME = 'PositionJobFamilies';
const positionJobFamiliesQueryString = `query ${QUERY_NAME}($cloudId: ID!, $fieldId: ID!, $rql: String, $after: String, $first: Int) {
  radar_fieldValues(
    cloudId: $cloudId
    uniqueFieldId: $fieldId
    rql: $rql
    first: $first
    after: $after
  ) { 
    totalCount
    pageInfo {
      endCursor
      hasNextPage
    }
    edges {
      cursor
      node {
        ... on RadarStringFieldValue {
          __typename
          isRestricted
          stringValue: value
        }
      }
    }
  }
}`;

export type PositionJobFamily = {
	cursor: string;
	node: {
		isRestricted: boolean;
		stringValue: string;
	};
};

export type PositionJobFamiliesResponse = {
	radar_fieldValues: {
		edges?: PositionJobFamily[];
	};
};

export type PositionJobFamiliesQueryVariables = {
	cloudId: string;
	first: number;
	fieldId: string;
	rql: string;
	after?: string;
};

export const positionJobFamiliesQuery: (
	cloudId: string,
	first: number | undefined,
	rql: string,
	after?: string,
) => Promise<AggResponse<PositionJobFamiliesResponse>> = (
	cloudId: string,
	first = 20,
	rql: string,
	after?: string,
) => {
	const fieldId = 'positionJobFamily';
	return positionJobFamiliesCache.inMemoryDecorator(
		[cloudId, first, rql, fieldId, after].filter(Boolean).join('-'),
		async () => {
			if (!cloudId) {
				return {
					data: {
						radar_fieldValues: {
							edges: [],
						},
					},
				};
			}

			const response = await fetchAgg<
				PositionJobFamiliesQueryVariables,
				PositionJobFamiliesResponse
			>({
				graphQuery: positionJobFamiliesQueryString,
				variables: { cloudId, first, rql, fieldId, after },
				operationName: QUERY_NAME,
			});

			const hasErrors = response.errors?.some((error) => error);

			if (hasErrors) {
				return Promise.reject(response);
			}
			return response;
		},
	);
};
